import {
  shouldIncludeAttachment,
  useGetAttachmentsByDocId,
  type Attachment as ApiAttachment,
} from "@/api/attachment";
import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { SupportedLocale } from "@/i18n";
import { ACCORDION_TITLE_CLASS } from "@/lib/accordion-content";
import { computeFieldCollectorNameForOverview } from "@/lib/computeFields";
import { Attachment, PublicationStatus, Tirage } from "@/lib/types";
import { ExemplaireThumbnail } from "@/pages/dashboard/shared/ExemplaireThumbnail";
import { Fragment, useMemo } from "react";
import { MergedDisplayData } from "../types";
import { TirageDrawer } from "./TirageDrawer";

interface TiragesAccordionProps {
  displayData: MergedDisplayData;
  isTirage: boolean;
  isPhotoModuleEnabled: boolean;
  locale: SupportedLocale;
  t: (key: string) => string;
}

interface TirageRowProps {
  tirage: Tirage;
  displayData: MergedDisplayData;
  isTirage: boolean;
  locale: SupportedLocale;
  t: (key: string) => string;
}

function TirageRow({
  tirage,
  displayData,
  isTirage,
  locale,
  t,
}: TirageRowProps) {
  const tirageSummaryParts = [
    tirage.tirageDescriptionNumeroEnregistrement,
    tirage.tirageDescriptionType?.[locale],
    tirage.tirageDescriptionDateCreation &&
      `(${tirage.tirageDescriptionDateCreation})`,
    computeFieldCollectorNameForOverview(
      {
        collectionneursDescriptionNom:
          tirage.collector?.collectionneursDescriptionNom ?? "",
        collectionneursDescriptionPrenom:
          tirage.collector?.collectionneursDescriptionPrenom ?? "",
        collectionneursDescriptionCollectionPublique:
          tirage.collector?.collectionneursDescriptionCollectionPublique ??
          false,
        collectionneursDescriptionVille:
          tirage.collector?.collectionneursDescriptionVille ?? "",
        collectionneursDescriptionVillePublished:
          tirage.collector?.collectionneursDescriptionVillePublished,
        collectionneursDescriptionPays:
          tirage.collector?.collectionneursDescriptionPays ?? "",
        collectionneursDescriptionPaysPublished:
          tirage.collector?.collectionneursDescriptionPaysPublished,
        showCity: true,
      },
      { language: locale, translation: t },
    ),
  ].filter(Boolean) as string[];

  // Prefer attachments already on the tirage; otherwise one fetch for the thumbnail.
  const listAttachments = tirage.attachments as ApiAttachment[] | undefined;
  const { data: fetchedAttachments } = useGetAttachmentsByDocId(
    listAttachments?.length ? undefined : tirage.id,
  );
  const attachments = listAttachments?.length
    ? listAttachments
    : fetchedAttachments;

  const thumbnailAttachments = useMemo(
    () =>
      (attachments?.filter((a) =>
        shouldIncludeAttachment(
          a,
          (tirage.tiragePublicationStatus ?? "draft") as PublicationStatus,
        ),
      ) ?? []) as Attachment[],
    [attachments, tirage.tiragePublicationStatus],
  );

  return (
    <div className="flex items-start gap-3 w-full">
      <ExemplaireThumbnail
        attachments={thumbnailAttachments}
        type="tirage"
        alt={tirage.tirageDescriptionNumeroEnregistrement ?? ""}
      />
      <TirageDrawer
        tirage={tirage}
        displayData={displayData}
        locale={locale}
        t={t}
        isTirage={isTirage}
        preloadedAttachments={attachments}
        trigger={
          <div className="flex-1 min-w-0 cursor-pointer hover:bg-gray-50 transition-colors rounded p-2 -m-2">
            <div className="font-normal text-sm line-clamp-4">
              {tirageSummaryParts.map((part, i) => (
                <Fragment key={i}>
                  {i > 0 && <br />}
                  {part}
                </Fragment>
              ))}
            </div>
          </div>
        }
      />
    </div>
  );
}

export function TiragesAccordion({
  displayData,
  isTirage,
  isPhotoModuleEnabled,
  locale,
  t,
}: TiragesAccordionProps) {
  // Only show Tirages accordion if:
  // 1. Not currently displaying a tirage itself
  // 2. Has tirages to display
  // 3. Photo module is enabled (which also implies artwork is photography type)
  if (
    isTirage ||
    !displayData.Tirage ||
    displayData.Tirage.length === 0 ||
    !isPhotoModuleEnabled
  ) {
    return null;
  }

  return (
    <AccordionItem value="tirages">
      <AccordionTrigger className={ACCORDION_TITLE_CLASS}>
        {t("singleArtworkSubtitleTirages")}
      </AccordionTrigger>
      <AccordionContent className="flex flex-col gap-4 w-full">
        {displayData.Tirage.map((tirage: Tirage) => (
          <TirageRow
            key={tirage.id}
            tirage={tirage}
            displayData={displayData}
            isTirage={isTirage}
            locale={locale}
            t={t}
          />
        ))}
      </AccordionContent>
    </AccordionItem>
  );
}
